import { createFileRoute, useNavigate, useRouter, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { claimAdminRole } from "@/lib/admin-access";

const searchSchema = z.object({
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/auth")({
  // Sign-in is inherently client-only (reads Supabase session from
  // localStorage, redirects on mount). Rendering it on the server produces
  // a shell that doesn't match the client tree once the lazy component
  // chunk resolves — React logs a hydration warning during client mount.
  // Disabling SSR here means the client renders the pendingComponent
  // fallback first and swaps to AuthPage without a hydration diff.
  ssr: false,
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Admin Sign In — Coffman Cove Lodging" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
  pendingComponent: AuthPending,
  errorComponent: AuthError,
});

function AuthError({ error, reset }: { error: Error; reset: () => void }) {
  // Catches chunk-load failures for the lazy AuthPage bundle, thrown
  // errors during mount (e.g. supabase client init), and any uncaught
  // render error inside the auth subtree. Keeps DOM/box sizing aligned
  // with AuthPage + AuthPending so swapping in doesn't shift layout,
  // and — crucially — never touches window/document at module scope,
  // so if this file re-hydrates the fallback is safe.
  const router = useRouter();
  useEffect(() => {
    // Surface to console so it lands in Server/Client logs.
    console.error("[/auth] render error:", error);
  }, [error]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[var(--color-parchment)]">
      <div
        role="alert"
        className="w-full max-w-md rounded-xl bg-white border border-border shadow-sm p-8 text-center"
      >
        <h1 className="font-display text-2xl text-[var(--color-navy-deep)]">
          Sign-in unavailable
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          We couldn't load the sign-in form. This is usually a temporary
          network hiccup while your browser fetches the latest version.
        </p>
        {import.meta.env.DEV && error?.message ? (
          <pre className="mt-4 max-h-32 overflow-auto rounded bg-muted/50 p-2 text-left text-[11px] text-muted-foreground">
            {error.message}
          </pre>
        ) : null}
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              // reset() clears the boundary; invalidate() re-runs the loader
              // and re-imports the lazy chunk, so a stale-chunk error after
              // a deploy self-heals on retry.
              reset();
              router.invalidate();
            }}
            className="w-full rounded-md bg-[var(--color-ember)] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-white hover:opacity-90"
          >
            Try again
          </button>
          <Link
            to="/"
            className="w-full rounded-md border border-input px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-navy-deep)] hover:bg-muted/40"
          >
            Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}

function AuthPending() {
  // Mirrors AuthPage's DOM structure and exact box sizing so the Suspense
  // swap is invisible: same wrapper padding, same heading/subtitle line
  // heights, same input+label+button rhythm, same footer links. Only the
  // text nodes are replaced with skeleton bars, and only those bars pulse
  // — the layout boxes themselves are static to avoid CLS.
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[var(--color-parchment)]">
      <div
        className="w-full max-w-md rounded-xl bg-white border border-border shadow-sm p-8"
        aria-busy="true"
        aria-live="polite"
      >
        <span className="sr-only">Loading sign-in form…</span>

        {/* h1 — font-display text-2xl → ~2rem line-height */}
        <div className="h-8 flex items-center">
          <div className="h-5 w-40 rounded bg-muted animate-pulse" />
        </div>
        {/* subtitle — text-sm, mt-2 */}
        <div className="mt-2 h-5 flex items-center">
          <div className="h-3.5 w-64 max-w-full rounded bg-muted/70 animate-pulse" />
        </div>

        {/* form — mt-6 space-y-4 */}
        <div className="mt-6 space-y-4">
          {[0, 1].map((i) => (
            <div key={i}>
              {/* label — text-xs mb-1 */}
              <div className="mb-1 h-4 flex items-center">
                <div className="h-3 w-16 rounded bg-muted/70 animate-pulse" />
              </div>
              {/* input — px-3 py-2 text-sm ≈ 38px tall */}
              <div className="h-[38px] w-full rounded-md border border-input bg-muted/40 animate-pulse" />
            </div>
          ))}
          {/* submit button — px-4 py-2.5 text-sm ≈ 40px tall */}
          <div className="h-[40px] w-full rounded-md bg-[var(--color-ember)]/40 animate-pulse" />
        </div>

        {/* mode toggle — mt-4 text-sm ≈ 20px tall */}
        <div className="mt-4 h-5 flex items-center justify-center">
          <div className="h-3.5 w-48 rounded bg-muted/70 animate-pulse" />
        </div>

        {/* back link — mt-6 text-xs ≈ 16px tall */}
        <div className="mt-6 h-4 flex items-center justify-center">
          <div className="h-3 w-24 rounded bg-muted/60 animate-pulse" />
        </div>
      </div>
    </div>
  );
}


function AuthPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) {
        const dest = typeof redirect === "string" && redirect.startsWith("/") ? redirect : "/admin/reviews";
        navigate({ to: dest });
      }
    });
  }, [navigate, redirect]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        await claimAdminRole();
        toast.success("Signed in");
        const dest = typeof redirect === "string" && redirect.startsWith("/") ? redirect : "/admin/reviews";
        navigate({ to: dest });
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            // Preserve the intended destination (e.g. the OAuth consent URL)
            // through the email confirmation round-trip.
            emailRedirectTo:
              typeof redirect === "string" && redirect.startsWith("/")
                ? `${window.location.origin}/auth?redirect=${encodeURIComponent(redirect)}`
                : `${window.location.origin}/auth`,
          },
        });
        if (error) throw error;
        toast.success("Account created. Check your email to confirm, then sign in.");
        setMode("signin");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[var(--color-parchment)]">
      <div className="w-full max-w-md rounded-xl bg-white border border-border shadow-sm p-8">
        <h1 className="font-display text-2xl">Admin Sign In</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          For staff reviewing photo submissions.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-input px-3 py-2 text-sm"
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-[var(--color-ember)] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-white disabled:opacity-60"
          >
            {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="mt-4 text-center text-sm">
          {mode === "signin" ? (
            <button className="text-muted-foreground hover:underline" onClick={() => setMode("signup")}>
              Need an account? Sign up
            </button>
          ) : (
            <button className="text-muted-foreground hover:underline" onClick={() => setMode("signin")}>
              Have an account? Sign in
            </button>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-xs text-muted-foreground hover:underline">← Back to site</Link>
        </div>
      </div>
    </div>
  );
}
